import { useMemo } from "react";
import type { IntlShape } from "react-intl";
import type { ConditionLabelFormatters, ConditionLabelLookup } from "./match-result-derive";
import { levelFromPercent } from "@/lib/skill-levels";
import { useRequirementsReferenceData } from "./use-requirements-reference-data";

export function useConditionLabelLookup(): ConditionLabelLookup {
  const refData = useRequirementsReferenceData();
  return useMemo(
    () => ({
      jobDepartments: new Map(refData.jobDepartments.map((d) => [d.value, d.label])),
      jobLocations: new Map(refData.jobLocations.map((l) => [l.value, l.label])),
      countries: new Map(refData.countries.map((c) => [c.value, c.label])),
      genders: new Map(refData.genders.map((g) => [g.value, g.label])),
      maritalStatuses: new Map(refData.maritalStatuses.map((m) => [m.value, m.label])),
      industries: new Map(refData.industries.map((i) => [i.value, i.label])),
    }),
    [refData],
  );
}


export function useConditionLabelFormatters(intl: IntlShape): ConditionLabelFormatters {
  return useMemo(() => {
    const thresholdWord = (operator: unknown): string => {
      switch (operator) {
        case "LE":
        case "LT":
          return intl.formatMessage({ id: "match.result.condition.thresholdMax", defaultMessage: "max." });
        case "EQ":
          return intl.formatMessage({ id: "match.result.condition.thresholdExact", defaultMessage: "genau" });
        case "NE":
          return intl.formatMessage({ id: "match.result.condition.thresholdNot", defaultMessage: "nicht" });
        case "GE":
        case "GT":
        default:
          return intl.formatMessage({ id: "match.result.condition.thresholdMin", defaultMessage: "mind." });
      }
    };

    const exact = (label: string, value: string, operator: unknown): string => {
      if (!value) return label;
      if (operator === "NE") {
        return intl.formatMessage(
          { id: "match.result.condition.exactNot", defaultMessage: "{label}: nicht {value}" },
          { label, value },
        );
      }
      return intl.formatMessage(
        { id: "match.result.condition.exact", defaultMessage: "{label}: {value}" },
        { label, value },
      );
    };

    return {
      labels: {
        department: intl.formatMessage({ id: "match.result.condition.department", defaultMessage: "Abteilung" }),
        location: intl.formatMessage({ id: "match.result.condition.location", defaultMessage: "Arbeitsstandort" }),
        country: intl.formatMessage({ id: "match.result.condition.country", defaultMessage: "Land" }),
        citizenship: intl.formatMessage({ id: "match.result.condition.citizenship", defaultMessage: "Staatsangehörigkeit" }),
        gender: intl.formatMessage({ id: "match.result.condition.gender", defaultMessage: "Geschlecht" }),
        maritalStatus: intl.formatMessage({ id: "match.result.condition.maritalStatus", defaultMessage: "Familienstand" }),
        age: intl.formatMessage({ id: "match.result.condition.age", defaultMessage: "Alter" }),
        annualSalary: intl.formatMessage({ id: "match.result.condition.annualSalary", defaultMessage: "Gehalt" }),
        certificate: intl.formatMessage({ id: "match.result.condition.certificate", defaultMessage: "Zertifikat" }),
        children: intl.formatMessage({ id: "match.result.condition.children", defaultMessage: "Kinder" }),
        city: intl.formatMessage({ id: "match.result.condition.city", defaultMessage: "Wohnort" }),
        driversLicense: intl.formatMessage({ id: "match.result.condition.driversLicense", defaultMessage: "Führerschein" }),
        education: intl.formatMessage({ id: "match.result.condition.education", defaultMessage: "Ausbildung" }),
        experience: intl.formatMessage({ id: "match.result.condition.experience", defaultMessage: "Berufserfahrung" }),
        industry: intl.formatMessage({ id: "match.result.condition.industry", defaultMessage: "Branche" }),
        nationality: intl.formatMessage({ id: "match.result.condition.nationality", defaultMessage: "Nationalität" }),
        expertise: intl.formatMessage({ id: "match.result.condition.expertise", defaultMessage: "Fachkompetenz" }),
        language: intl.formatMessage({ id: "match.result.condition.language", defaultMessage: "Sprache" }),
      },
      exact,
      present: (label: string): string =>
        intl.formatMessage(
          { id: "match.result.condition.present", defaultMessage: "{label}: Vorhanden" },
          { label },
        ),
      threshold: (label: string, value: string, operator: unknown): string => {
        if (!value) return label;
        if (typeof operator === "string" && operator.length > 0) {
          return intl.formatMessage(
            { id: "match.result.condition.threshold", defaultMessage: "{label}: {word} {value}" },
            { label, word: thresholdWord(operator), value },
          );
        }
        return exact(label, value, operator);
      },
      years: (years: number): string =>
        intl.formatMessage(
          {
            id: "match.result.ranking.yearsValue",
            defaultMessage: "{years, plural, one {# Jahr} other {# Jahre}}",
          },
          { years },
        ),
      languageName: (tag: string): string => {
        // Sprach-Tag ("de") -> lokalisierter Name ("Deutsch"), wie in lib/language-options.ts.
        try {
          const name = new Intl.DisplayNames([intl.locale], { type: "language" }).of(tag);
          return name && name !== tag ? name : tag;
        } catch {
          return tag;
        }
      },
      skillLevelName: (level) => intl.formatMessage({ id: level.nameId, defaultMessage: level.name }),
    };
  }, [intl]);
}

/**
 * Ist-Wert einer Person zu einem Kriterium: genesis liefert den Rohwert (Id/Prozent/Zahl), hier
 * wird er per __typename mit den vorhandenen Lookups aufgeloest (Ids -> Label, Level-Prozent ->
 * Stufenname). Liegt hier statt in einer
 * Panel-Komponente, weil der PDF-Export dieselbe Aufloesung braucht und sie im Host vorformatiert
 * (@adr SPARK-0044-match-result-pdfs-use-a-dedicated-spa-renderer@3).
 */
export function formatConditionActual(
  condition: unknown,
  actual: string | null,
  labelLookup: ConditionLabelLookup,
  intl: IntlShape,
): string | null {
  if (!actual) return null;
  const typename =
    condition && typeof condition === "object" ? (condition as { __typename?: string }).__typename : "";
  switch (typename) {
    case "GenderCondition":
      return labelLookup.genders.get(actual) ?? actual;
    case "CountryCondition":
    case "CitizenshipCondition":
      return labelLookup.countries.get(actual) ?? actual;
    case "JobDepartmentCondition":
      return labelLookup.jobDepartments.get(actual) ?? actual;
    case "JobLocationCondition":
      return labelLookup.jobLocations.get(actual) ?? actual;
    case "MaritalCondition":
      return labelLookup.maritalStatuses.get(actual) ?? actual;
    case "ExpertiseCondition": {
      const pct = Number(actual);
      if (Number.isFinite(pct) && pct <= 0) {
        return intl.formatMessage({ id: "match.result.condition.presentActual", defaultMessage: "Vorhanden" });
      }
      return Number.isFinite(pct) ? levelFromPercent(pct)?.name ?? actual : actual;
    }
    case "IndustryExperienceCondition": {
      const years = Number(actual);
      return Number.isFinite(years)
        ? intl.formatMessage(
            {
              id: "match.result.ranking.yearsValue",
              defaultMessage: "{years, plural, one {# Jahr} other {# Jahre}}",
            },
            { years },
          )
        : actual;
    }
    default:
      return actual;
  }
}
